<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, inject } from 'vue'
import { isProjectSensitiveKey, sensitiveFieldsKey } from '@/utils/sensitive-fields'

interface Props {
  label?: string
  value?: string | number | boolean | null
  sensitiveField?: string
  format?: 'number' | 'date' | 'currency' | 'euros' | 'percent' | 'enum' | 'boolean' | 'string'
  prefix?: string
  suffix?: string
  enumTranslation?: string
  css?: string
  lines?: number
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

const props = withDefaults(defineProps<Props>(), {
  format: 'number',
  css: 'font-medium',
})

const { t, n, d } = useI18n()

const hasDisplayValue = (value: Props['value']): value is string | number | boolean => {
  return value !== undefined && value !== null
}

const isNumericValue = (value: Props['value']): value is string | number => {
  return (typeof value === 'string' || typeof value === 'number') && !Number.isNaN(Number(value))
}

const isDateValue = (value: Props['value']): value is string | number => {
  return typeof value === 'string' || typeof value === 'number'
}

const isNumberValue = (value: Props['value']): value is number => {
  return typeof value === 'number'
}

const sensitiveFields = inject(
  sensitiveFieldsKey,
  computed(() => new Set<string>()),
)

const isProjectSensitive = inject(
  isProjectSensitiveKey,
  computed(() => false),
)

const shouldDisplaySensitivePlaceholder = computed(() => {
  if (!props.sensitiveField || !isProjectSensitive.value) {
    return false
  }
  return sensitiveFields.value.has(props.sensitiveField)
})
</script>

<template>
  <div class="flex flex-col">
    <div v-if="label" class="text-surface-400 text-xs font-medium tracking-wider uppercase">
      {{ label }}
    </div>
    <div :class="[css, lines ? `line-clamp-${lines}` : '']">
      <slot />
      <span v-if="prefix && hasDisplayValue(value)">{{ prefix }}</span>
      <span v-if="shouldDisplaySensitivePlaceholder">
        <span class="bg-surface-300/70 inline-block h-4 w-24 rounded align-middle blur-[1px]"> &nbsp; </span>
      </span>
      <span v-else-if="value === undefined || value === null"> &mdash; </span>
      <span v-else-if="format === 'boolean'">
        {{ value ? t('yes') : t('no') }}
      </span>
      <span v-else-if="format === 'enum' && enumTranslation">
        <template v-if="t(`enums.${enumTranslation}.${value}`) !== `enums.${enumTranslation}.${value}`">
          {{ t(`enums.${enumTranslation}.${value}`) }}
        </template>
      </span>
      <span v-else-if="format === 'number' && isNumericValue(value)">
        {{ n(Number(value), { minimumFractionDigits, maximumFractionDigits }) }}
      </span>
      <span v-else-if="format === 'date' && isDateValue(value)">
        {{ d(value) }}
      </span>
      <span v-else-if="format === 'currency' && isNumberValue(value)">
        {{ n(value, { style: 'currency', currency: 'EUR', minimumFractionDigits, maximumFractionDigits }) }}
      </span>
      <span v-else-if="format === 'euros' && isNumberValue(value)">
        {{ n(value, { style: 'currency', currency: 'EUR', minimumFractionDigits, maximumFractionDigits }) }}
      </span>
      <span v-else-if="format === 'percent' && isNumberValue(value)">
        {{ n(value, { style: 'percent', minimumFractionDigits, maximumFractionDigits }) }}
      </span>
      <span v-else>
        {{ value }}
      </span>
      <span v-if="suffix && hasDisplayValue(value)">{{ suffix }}</span>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "yes": "Yes",
    "no": "No"
  },
  "de": {
    "yes": "Ja",
    "no": "Nein"
  }
}
</i18n>
